import { useState } from 'react';
import { post, get } from '../helpers/apiHelper';

export const useMarketingChain = (userId) => {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const startAnalysis = async (input) => {
        setLoading(true);
        setError(null);

        try {
            console.log('Enviando solicitud de análisis:', { ...input, userId });

            const response = await post('agents/marketing-chain/analyze', {
                ...input,
                userId
            });

            console.log('Respuesta del servidor:', response);
            console.log('Tipo de respuesta:', typeof response);
            console.log('Keys de respuesta:', Object.keys(response));

            if (response.status !== 'success') {
                throw new Error(response.message || 'Error al iniciar análisis');
            }

            // Crear objeto de sesión con la estructura esperada
            const sessionData = {
                sessionId: response.sessionId,
                status: 'analyzing',
                userId: userId,
                createdAt: new Date().toISOString(),
                ...response.data
            };

            setSession(sessionData);
            return response;
        } catch (err) {
            console.error('Error detallado:', err);
            let errorMessage = 'Error al iniciar análisis';

            if (err.response) {
                // Error de respuesta del servidor
                errorMessage = err.response.data?.message || err.response.statusText || 'Error del servidor';
            } else if (err.request) {
                // Error de red
                errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
            } else if (err.message) {
                // Error personalizado
                errorMessage = err.message;
            }

            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const selectObjective = async (objectiveId) => {
        if (!session) {
            throw new Error('No hay sesión activa');
        }

        setLoading(true);
        setError(null);

        try {
            const response = await post('agents/marketing-chain/select-objective', {
                sessionId: session.sessionId,
                objectiveId
            });

            console.log('Respuesta de selección de objetivo:', response);
            console.log('Tipo de respuesta:', typeof response);
            console.log('Keys de respuesta:', Object.keys(response));

            // Si la respuesta no tiene status, asumir que es exitosa
            if (response.status && response.status !== 'success') {
                throw new Error(response.message || 'Error al seleccionar objetivo');
            }

            // Actualizar sesión con nuevas estrategias
            const updatedSession = await getSessionStatus(session.sessionId);
            setSession(updatedSession);
            return response;
        } catch (err) {
            const errorMessage = err.message || 'Error al seleccionar objetivo';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const selectStrategy = async (strategyId) => {
        if (!session) {
            throw new Error('No hay sesión activa');
        }

        setLoading(true);
        setError(null);

        try {
            const response = await post('agents/marketing-chain/select-strategy', {
                sessionId: session.sessionId,
                strategyId
            });

            console.log('Respuesta de selección de estrategia:', response);
            console.log('Tipo de respuesta:', typeof response);
            console.log('Keys de respuesta:', Object.keys(response));

            // Si la respuesta no tiene status, asumir que es exitosa
            if (response.status && response.status !== 'success') {
                throw new Error(response.message || 'Error al seleccionar estrategia');
            }

            // Actualizar sesión con nuevas tácticas
            const updatedSession = await getSessionStatus(session.sessionId);
            setSession(updatedSession);
            return response;
        } catch (err) {
            const errorMessage = err.message || 'Error al seleccionar estrategia';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const getSessionStatus = async (sessionId, signal = null) => {
        try {
            const response = await get(`agents/marketing-chain/status/${sessionId}`, signal);
            setSession(response);
            return response;
        } catch (err) {
            // No establecer error si es una cancelación
            if (err.name === 'AbortError') {
                throw err;
            }
            const errorMessage = err.message || 'Error al obtener estado de sesión';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const getUserSessions = async () => {
        try {
            const response = await get(`agents/marketing-chain/user-sessions/${userId}`);
            return response;
        } catch (err) {
            const errorMessage = err.message || 'Error al obtener sesiones del usuario';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    // Nuevos métodos para historial y reanudación de sesiones
    const getHistory = async () => {
        try {
            const response = await get(`agents/marketing-chain/history/${userId}`);
            return response;
        } catch (err) {
            const errorMessage = err.message || 'Error al obtener historial';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const getHistoryByMarca = async (marcaId) => {
        try {
            const response = await get(`agents/marketing-chain/history/${userId}/marca/${marcaId}`);
            return response;
        } catch (err) {
            const errorMessage = err.message || 'Error al obtener historial por marca';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const checkResumeSession = async () => {
        try {
            const response = await get(`agents/marketing-chain/resume/${userId}`);
            return response; // Puede ser null si no hay sesión pendiente
        } catch (err) {
            const errorMessage = err.message || 'Error al verificar sesión pendiente';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const isSessionRecent = (sessionDate, maxDays = 7) => {
        const sessionTime = new Date(sessionDate).getTime();
        const currentTime = new Date().getTime();
        const daysDiff = (currentTime - sessionTime) / (1000 * 60 * 60 * 24);
        return daysDiff <= maxDays;
    };

    const shouldUseExistingSession = (session) => {
        if (!session) return false;

        // Verificar si la sesión es reciente (menos de 7 días)
        if (!isSessionRecent(session.createdAt)) return false;

        // Verificar si la sesión está en un estado válido para continuar
        const validStates = ['objectives_ready', 'strategy_ready', 'tactics_ready'];
        return validStates.includes(session.status);
    };

    const deleteSession = async (sessionId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}/agents/marketing-chain/session/${sessionId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('auth-storage-petitprime'))?.state?.token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al eliminar sesión');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            const errorMessage = err.message || 'Error al eliminar sesión';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const clearError = () => {
        setError(null);
    };

    const resetSession = () => {
        setSession(null);
        setError(null);
    };

    const getLastStrategy = async () => {
        try {
            const response = await get(`agents/marketing-chain/last-strategy/${userId}`);
            return response;
        } catch (error) {
            console.error('Error obteniendo última estrategia:', error);
            return null;
        }
    };

    const getLastStrategyByMarca = async (marcaId) => {
        try {
            const response = await get(`agents/marketing-chain/last-strategy/${userId}/marca/${marcaId}`);
            return response;
        } catch (error) {
            console.error('Error obteniendo última estrategia por marca:', error);
            return null;
        }
    };

    return {
        session,
        loading,
        error,
        startAnalysis,
        selectObjective,
        selectStrategy,
        getSessionStatus,
        getUserSessions,
        getHistory,
        getHistoryByMarca,
        checkResumeSession,
        shouldUseExistingSession,
        deleteSession,
        clearError,
        resetSession,
        setSession,
        getLastStrategy,
        getLastStrategyByMarca
    };
}; 